<template>
  <div v-if="showErrorResolver && currentError" class="error-resolver" :class="{ 'dark-mode': isDarkMode }">
    <!-- Error Header -->
    <div class="error-header" :class="`severity-${severity}`">
      <div class="error-icon">
        <i class="cil-warning"></i>
      </div>
      <div class="error-title">
        <h3>{{ errorTitle }}</h3>
        <span class="error-type">{{ errorTypeFriendly }}</span>
      </div>
      <button class="close-btn" @click="close" aria-label="Close">
        <i class="cil-x"></i>
      </button>
    </div>

    <!-- User-Friendly Message -->
    <div class="error-message">
      <p>{{ userFriendlyMessage }}</p>
    </div>

    <!-- Solutions -->
    <div v-if="solutions && solutions.length > 0" class="solutions-section">
      <h4 class="section-title">
        <i class="cil-lightbulb"></i>
        Suggested Solutions
      </h4>
      
      <div class="solutions-list">
        <div
          v-for="(solution, index) in solutions"
          :key="index"
          class="solution-card"
          :class="{ 'guided-workflow': solution.type === 'guided_workflow' }"
        >
          <div class="solution-header">
            <i :class="solution.icon || 'cil-check'"></i>
            <h5>{{ solution.title }}</h5>
            <span v-if="solution.automated" class="auto-badge">Auto</span>
          </div>
          
          <p class="solution-description">{{ solution.description }}</p>
          
          <!-- Guided Workflow Steps -->
          <div v-if="solution.steps && solution.steps.length > 0" class="workflow-steps">
            <div
              v-for="step in solution.steps"
              :key="step.step"
              class="workflow-step"
              :class="{ completed: completedSteps.includes(step.step) }"
            >
              <div class="step-number">{{ step.step }}</div>
              <div class="step-content">
                <h6>{{ step.title }}</h6>
                <p>{{ step.description }}</p>
              </div>
              <button
                v-if="!completedSteps.includes(step.step)"
                @click="markStepComplete(step.step)"
                class="step-check-btn"
                aria-label="Mark complete"
              >
                <i class="cil-check"></i>
              </button>
            </div>
          </div>
          
          <!-- Solution Action Button -->
          <button
            v-if="solution.action || solution.automated"
            @click="executeSolution(solution)"
            class="solution-action-btn"
            :class="{ 'btn-primary': solution.automated, 'btn-secondary': !solution.automated }"
          >
            <i :class="solution.icon || 'cil-play'"></i>
            {{ solution.type === 'retry' ? 'Try Again' : solution.type === 'refresh' ? 'Refresh Page' : solution.type === 'login' ? 'Log In' : 'Apply Solution' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Similar Errors -->
    <div v-if="similarErrors && similarErrors.length > 0" class="similar-errors-section">
      <h4 class="section-title">
        <i class="cil-history"></i>
        Similar Errors Resolved
      </h4>
      
      <div class="similar-errors-list">
        <div v-for="(error, index) in similarErrors" :key="index" class="similar-error">
          <i class="cil-check-circle success"></i>
          <div class="similar-error-content">
            <p class="error-msg">{{ error.message }}</p>
            <p v-if="error.resolution" class="resolution">{{ error.resolution }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Technical Details (Collapsible) -->
    <div class="technical-details">
      <button @click="showTechnical = !showTechnical" class="technical-toggle">
        <i :class="showTechnical ? 'cil-chevron-bottom' : 'cil-chevron-right'"></i>
        Technical Details
      </button>
      
      <div v-if="showTechnical" class="technical-content">
        <p><strong>Error Type:</strong> {{ currentError.type }}</p>
        <p v-if="currentError.status"><strong>Status Code:</strong> {{ currentError.status }}</p>
        <p v-if="currentError.url"><strong>URL:</strong> {{ currentError.url }}</p>
        <p v-if="currentError.method"><strong>Method:</strong> {{ currentError.method }}</p>
        <p><strong>Original Message:</strong> {{ currentError.message }}</p>
        <p><strong>Timestamp:</strong> {{ formatTimestamp(currentError.timestamp) }}</p>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="error-actions">
      <button v-if="canRetry" @click="retryRequest" class="btn btn-primary" :disabled="isRetrying">
        <i class="cil-reload"></i>
        {{ isRetrying ? 'Retrying...' : 'Retry Request' }}
      </button>
      
      <button v-if="shouldContactSupport" @click="contactSupport" class="btn btn-outline">
        <i class="cil-envelope-open"></i>
        Contact Support
      </button>
      
      <button @click="openAIChat" class="btn btn-outline">
        <i class="cil-comment-bubble"></i>
        Ask AI Assistant
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'ErrorResolver',

  data() {
    return {
      showTechnical: false,
      isRetrying: false,
      completedSteps: [],
    };
  },

  computed: {
    ...mapGetters('aiAssistant', [
      'currentError',
      'errorTranslation',
      'showErrorResolver',
    ]),

    isDarkMode() {
      return this.$store.state.darkMode;
    },

    severity() {
      return this.errorTranslation?.severity || 'medium';
    },

    errorTitle() {
      const titles = {
        validation_error: 'Invalid Information',
        authentication_error: 'Session Expired',
        permission_error: 'Access Denied',
        not_found_error: 'Not Found',
        server_error: 'Server Error',
        network_error: 'Connection Problem',
        database_error: 'Database Issue',
        file_upload_error: 'Upload Failed',
        unknown_error: 'Something Went Wrong',
      };
      return titles[this.currentError?.type] || 'Error Occurred';
    },

    errorTypeFriendly() {
      const types = {
        validation_error: 'Validation Error',
        authentication_error: 'Authentication Required',
        permission_error: 'Permission Denied',
        not_found_error: 'Resource Not Found',
        server_error: 'Server Error',
        network_error: 'Network Error',
        database_error: 'Database Error',
        file_upload_error: 'Upload Error',
        unknown_error: 'Unknown Error',
      };
      return types[this.currentError?.type] || 'Error';
    },

    userFriendlyMessage() {
      return this.errorTranslation?.user_friendly_message || this.currentError?.message || 'An unexpected error occurred.';
    },

    solutions() {
      return this.errorTranslation?.solutions || [];
    },

    similarErrors() {
      return this.errorTranslation?.similar_errors || [];
    },

    canRetry() {
      return this.errorTranslation?.can_retry || this.currentError?.canRetry || false;
    },

    shouldContactSupport() {
      return this.errorTranslation?.contact_support || this.severity === 'high';
    },
  },

  methods: {
    ...mapActions('aiAssistant', [
      'hideErrorResolver',
      'retryRequest',
      'open',
      'sendMessage',
    ]),

    close() {
      this.hideErrorResolver();
      this.completedSteps = [];
      this.showTechnical = false;
      this.isRetrying = false;
    },

    async executeSolution(solution) {
      if (solution.type === 'retry' || solution.type === 'refresh') {
        await this.retryRequest();
      } else if (solution.type === 'login') {
        window.location.href = '/login';
      } else if (solution.type === 'go_back') {
        this.$router.back();
      } else if (solution.type === 'support') {
        this.contactSupport();
      } else if (solution.action) {
        if (solution.action.startsWith('http') || solution.action.startsWith('/')) {
          // Validate URL before navigation
          if (solution.action.startsWith('/')) {
            // Internal path - validate and use router
            if (this.isValidInternalPath(solution.action)) {
              this.$router.push(solution.action);
            } else {
              console.warn('Invalid internal path:', solution.action);
            }
          } else {
            // External URL - validate before redirect
            if (this.isValidURL(solution.action)) {
              window.location.href = solution.action;
            } else {
              console.warn('Invalid or untrusted URL:', solution.action);
            }
          }
        } else {
          // Execute predefined safe actions
          this.executeSafeAction(solution.action);
        }
      }
    },

    executeSafeAction(actionString) {
      // Map of safe predefined actions
      const safeActions = {
        'location.reload()': () => window.location.reload(),
        'window.location.reload()': () => window.location.reload(),
        'history.back()': () => window.history.back(),
        'window.history.back()': () => window.history.back(),
        'router.back()': () => this.$router.back(),
        'router.push("/")': () => this.$router.push('/'),
        'router.push("/dashboard")': () => this.$router.push('/dashboard'),
        'router.push("/login")': () => this.$router.push('/login'),
      };

      try {
        // Check if action is in safe list
        if (safeActions[actionString]) {
          safeActions[actionString]();
        } else {
          // Try to parse as router push with dynamic path
          const routerPushMatch = actionString.match(/router\.push\(['"](.+?)['"]\)/);
          if (routerPushMatch) {
            const path = routerPushMatch[1];
            if (this.isValidInternalPath(path)) {
              this.$router.push(path);
            } else {
              console.warn('Invalid or unsafe path detected:', path);
            }
          } else {
            console.warn('Attempted to execute unsafe action:', actionString);
            console.warn('Only predefined safe actions are allowed');
          }
        }
      } catch (error) {
        console.error('Failed to execute solution action:', error);
      }
    },

    isValidInternalPath(path) {
      // Validate that the path is a safe internal route
      if (typeof path !== 'string' || !path) {
        return false;
      }

      // Must start with / (relative path)
      if (!path.startsWith('/')) {
        return false;
      }

      // Reject paths with protocol (http://, https://, //)
      if (path.match(/^(\/\/|https?:\/\/)/i)) {
        return false;
      }

      // Reject paths with dangerous characters
      if (path.match(/[<>"'`]/)) {
        return false;
      }

      // Reject paths trying to navigate to external domains
      try {
        const url = new URL(path, window.location.origin);
        // Ensure same origin
        if (url.origin !== window.location.origin) {
          return false;
        }
      } catch (e) {
        // If URL parsing fails, reject
        return false;
      }

      return true;
    },

    isValidURL(urlString) {
      // Validate URL for safe navigation
      if (typeof urlString !== 'string' || !urlString) {
        return false;
      }

      // Whitelist of allowed protocols
      const allowedProtocols = ['http:', 'https:'];

      try {
        const url = new URL(urlString, window.location.origin);

        // Check protocol
        if (!allowedProtocols.includes(url.protocol)) {
          return false;
        }

        // For absolute URLs, only allow same origin or explicitly whitelisted domains
        if (urlString.startsWith('http')) {
          // Allow same origin
          if (url.origin === window.location.origin) {
            return true;
          }

          // Whitelist of trusted external domains (add your domains here)
          const trustedDomains = [
            // 'trusted-domain.com',
          ];

          const hostname = url.hostname.toLowerCase();
          const isTrusted = trustedDomains.some(domain => 
            hostname === domain || hostname.endsWith('.' + domain)
          );

          if (!isTrusted) {
            console.warn('Blocked navigation to untrusted domain:', hostname);
            return false;
          }
        }

        return true;
      } catch (e) {
        return false;
      }
    },

    async retryRequest() {
      this.isRetrying = true;
      try {
        await this.retryRequest();
        this.close();
        this.$toasted?.success('Request succeeded!');
      } catch (error) {
        this.$toasted?.error('Retry failed. Please try again.');
      } finally {
        this.isRetrying = false;
      }
    },

    contactSupport() {
      // Navigate to support page or open support modal
      this.$router.push('/support');
    },

    openAIChat() {
      this.close();
      this.open();
      
      // Send error context to AI
      const errorMessage = `I encountered this error: "${this.currentError.message}". Can you help me resolve it?`;
      this.sendMessage({
        message: errorMessage,
        context: this.currentError.context || {},
      });
    },

    markStepComplete(stepNumber) {
      if (!this.completedSteps.includes(stepNumber)) {
        this.completedSteps.push(stepNumber);
      }
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return date.toLocaleString();
    },
  },
};
</script>

<style scoped lang="scss">
.error-resolver {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  overflow-y: auto;
  z-index: 9999;
  animation: slideIn 0.3s ease;

  &.dark-mode {
    background: #1e1e1e;
    color: #e0e0e0;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translate(-50%, -45%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
}

.error-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  border-radius: 16px 16px 0 0;

  &.severity-high {
    background: linear-gradient(135deg, #ff6b6b 0%, #c92a2a 100%);
    color: white;
    border-bottom-color: transparent;
  }

  &.severity-medium {
    background: linear-gradient(135deg, #ffd43b 0%, #fab005 100%);
    color: #333;
    border-bottom-color: transparent;
  }

  &.severity-low {
    background: linear-gradient(135deg, #74c0fc 0%, #339af0 100%);
    color: white;
    border-bottom-color: transparent;
  }

  .error-icon {
    font-size: 32px;
  }

  .error-title {
    flex: 1;

    h3 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
    }

    .error-type {
      font-size: 12px;
      opacity: 0.9;
    }
  }

  .close-btn {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
      transform: scale(1.1);
    }

    i {
      font-size: 18px;
    }
  }
}

.error-message {
  padding: 20px;
  font-size: 16px;
  line-height: 1.6;
}

.solutions-section,
.similar-errors-section {
  padding: 20px;
  border-top: 1px solid #e0e0e0;

  .dark-mode & {
    border-top-color: #3a3a3a;
  }

  .section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 16px 0;
    font-size: 16px;
    font-weight: 600;
    color: #667eea;

    .dark-mode & {
      color: #8c9eff;
    }
  }
}

.solutions-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.solution-card {
  padding: 16px;
  background: #f5f5f5;
  border-radius: 12px;
  border-left: 4px solid #667eea;
  transition: all 0.2s ease;

  .dark-mode & {
    background: #2a2a2a;
  }

  &.guided-workflow {
    border-left-color: #51cf66;
  }

  &:hover {
    transform: translateX(4px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
  }

  .solution-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;

    i {
      color: #667eea;
      font-size: 18px;
    }

    h5 {
      flex: 1;
      margin: 0;
      font-size: 14px;
      font-weight: 600;
    }

    .auto-badge {
      background: #51cf66;
      color: white;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 600;
    }
  }

  .solution-description {
    margin: 0 0 12px 0;
    font-size: 14px;
    opacity: 0.8;
  }

  .solution-action-btn {
    width: 100%;
    padding: 10px;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    &.btn-primary {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
      }
    }

    &.btn-secondary {
      background: #e0e0e0;
      color: #333;

      .dark-mode & {
        background: #3a3a3a;
        color: #e0e0e0;
      }

      &:hover {
        background: #d0d0d0;
      }
    }
  }
}

.workflow-steps {
  margin: 16px 0;
}

.workflow-step {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  margin-bottom: 8px;
  transition: all 0.2s ease;

  .dark-mode & {
    background: #1e1e1e;
  }

  &.completed {
    opacity: 0.6;

    .step-number {
      background: #51cf66;
    }
  }

  .step-number {
    width: 32px;
    height: 32px;
    background: #667eea;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    flex-shrink: 0;
  }

  .step-content {
    flex: 1;

    h6 {
      margin: 0 0 4px 0;
      font-size: 14px;
      font-weight: 600;
    }

    p {
      margin: 0;
      font-size: 13px;
      opacity: 0.8;
    }
  }

  .step-check-btn {
    width: 32px;
    height: 32px;
    background: #e0e0e0;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:hover {
      background: #51cf66;
      color: white;
      transform: scale(1.1);
    }
  }
}

.similar-errors-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.similar-error {
  display: flex;
  gap: 12px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 8px;

  .dark-mode & {
    background: #2a2a2a;
  }

  i.success {
    color: #51cf66;
    font-size: 20px;
    flex-shrink: 0;
  }

  .similar-error-content {
    flex: 1;

    .error-msg {
      margin: 0 0 4px 0;
      font-size: 13px;
      font-weight: 500;
    }

    .resolution {
      margin: 0;
      font-size: 12px;
      opacity: 0.7;
      font-style: italic;
    }
  }
}

.technical-details {
  padding: 20px;
  border-top: 1px solid #e0e0e0;

  .dark-mode & {
    border-top-color: #3a3a3a;
  }

  .technical-toggle {
    background: none;
    border: none;
    padding: 0;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    color: inherit;

    &:hover {
      color: #667eea;
    }
  }

  .technical-content {
    margin-top: 12px;
    padding: 12px;
    background: #f5f5f5;
    border-radius: 8px;
    font-size: 13px;

    .dark-mode & {
      background: #2a2a2a;
    }

    p {
      margin: 6px 0;
      word-break: break-word;

      strong {
        color: #667eea;
        margin-right: 8px;
      }
    }
  }
}

.error-actions {
  padding: 20px;
  display: flex;
  gap: 12px;
  border-top: 1px solid #e0e0e0;
  flex-wrap: wrap;

  .dark-mode & {
    border-top-color: #3a3a3a;
  }

  .btn {
    padding: 12px 24px;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 8px;
    border: none;

    &.btn-primary {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;

      &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }

    &.btn-outline {
      background: transparent;
      border: 2px solid #667eea;
      color: #667eea;

      &:hover {
        background: #667eea;
        color: white;
      }
    }
  }
}

@media (max-width: 768px) {
  .error-resolver {
    width: 95%;
    max-height: 90vh;
  }

  .error-actions {
    flex-direction: column;

    .btn {
      width: 100%;
      justify-content: center;
    }
  }
}
</style>
